// In-app replacement for window.confirm(): `confirmDialog({...})` resolves a
// Promise<boolean> and renders a styled Modal via the singleton <ConfirmHost />
// mounted at the app root. Call sites stay one-liners:
//
//     if (!(await confirmDialog({ message: "Delete X?", danger: true }))) return;
//
// If the host isn't mounted (tests, detached surfaces) it degrades to the
// native confirm so destructive actions are never silently unguarded.

import { useEffect, useState } from "react";
import { Modal } from "./Modal";

export interface ConfirmOptions {
  /** Dialog heading; defaults to "Are you sure?". */
  title?: string;
  message: string;
  /** Confirm button label; defaults to "Confirm" (or "Delete" when danger). */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive. */
  danger?: boolean;
}

interface PendingConfirm {
  opts: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

let enqueue: ((req: PendingConfirm) => void) | null = null;

/** Ask the user to confirm. Resolves true on confirm, false on cancel/escape. */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  if (!enqueue) return Promise.resolve(window.confirm(opts.message));
  return new Promise<boolean>((resolve) => enqueue!({ opts, resolve }));
}

/** Mount ONCE at the app root. Renders the active confirm request, if any. */
export function ConfirmHost() {
  const [queue, setQueue] = useState<PendingConfirm[]>([]);

  useEffect(() => {
    enqueue = (req) => setQueue((q) => [...q, req]);
    return () => {
      enqueue = null;
    };
  }, []);

  const current = queue[0];
  if (!current) return null;

  function settle(ok: boolean) {
    current!.resolve(ok);
    setQueue((q) => q.slice(1));
  }

  const { opts } = current;
  return (
    <Modal
      title={opts.title ?? "Are you sure?"}
      onClose={() => settle(false)}
      footer={
        <>
          <button className="btn" onClick={() => settle(false)}>
            {opts.cancelLabel ?? "Cancel"}
          </button>
          <button
            className={`btn ${opts.danger ? "danger" : "primary"}`}
            autoFocus
            onClick={() => settle(true)}
          >
            {opts.confirmLabel ?? (opts.danger ? "Delete" : "Confirm")}
          </button>
        </>
      }
    >
      <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{opts.message}</p>
    </Modal>
  );
}
